/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useRef, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { QueryWorkbenchFloatingPanelFrame } from '../../../../../components/QueryWorkbench';
import { useAsideController, type Message } from '../../../../../contexts/AsideContext';
import type { CyphieMode } from '../../../../../state/LabsAtoms';
import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
import { messages } from './messages';
import { useNL2Cypher } from './useNL2Cypher';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		backgroundColor: token('elevation.surface'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
		borderTopLeftRadius: token('radius.large'),
	},
	containerWorkbench: {
		borderLeftWidth: 0,
		borderTopLeftRadius: 0,
	},
});

export const GraphExplorerChatAside = ({
	external = false,
	onUseCypher,
	history,
}: {
	external?: boolean;
	onUseCypher?: (query: string) => void;
	history?: { messages: Message[]; addMessage: (message: Message) => void };
} = {}): JSX.Element => {
	const aside = useAsideController();
	const { close } = aside;
	const chatMessages = history?.messages ?? aside.messages;
	const addMessage = history?.addMessage ?? aside.addMessage;
	const { sendMessage, isLoading } = useNL2Cypher(external);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const hasInitialized = useRef(false);
	const isMounted = useRef(true);
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);
	const [selectedMode, setSelectedMode] = useState<CyphieMode>('default');
	const { formatMessage } = useIntl();

	// Auto-scroll to bottom when new messages arrive
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [chatMessages]);

	useEffect(() => {
		if (chatMessages.length === 0 && !hasInitialized.current) {
			hasInitialized.current = true;
			addMessage({
				id: 'welcome-message',
				role: 'assistant',
				content: formatMessage(external ? messages.queryAssistantWelcome : messages.welcomeMessage),
			});
		}
	}, [chatMessages.length, addMessage, formatMessage, external]);

	const handleSend = useCallback(
		async (content: string) => {
			const userMessage: Message = {
				id: `user-${Date.now()}`,
				role: 'user',
				content,
			};
			addMessage(userMessage);

			const result = await sendMessage(content, [...chatMessages, userMessage], selectedMode);
			if (!isMounted.current) {
				return;
			}

			addMessage({
				id: `assistant-${Date.now()}`,
				role: 'assistant',
				content: result.content,
				metadata: {
					...(result.rawCypherQuery ? { rawCypherQuery: result.rawCypherQuery } : {}),
					cyphieMode: selectedMode,
				},
			});
		},
		[addMessage, sendMessage, chatMessages, selectedMode],
	);

	const content = (
		<Box xcss={cx(styles.container, external && styles.containerWorkbench)}>
			<ChatHeader
				onClose={close}
				title={external ? formatMessage(messages.queryAssistantHeading) : undefined}
			/>

			<ChatMessageList
				chatMessages={chatMessages}
				onUseCypher={onUseCypher}
				assistantName={external ? formatMessage(messages.queryAssistantHeading) : undefined}
				isLoading={isLoading}
				messagesEndRef={messagesEndRef}
			/>

			<ChatInput
				external={external}
				onSend={handleSend}
				disabled={isLoading}
				selectedMode={selectedMode}
				onModeChange={setSelectedMode}
			/>
		</Box>
	);

	return external ? (
		<QueryWorkbenchFloatingPanelFrame>{content}</QueryWorkbenchFloatingPanelFrame>
	) : (
		content
	);
};
